import Vue from 'vue'
import Vuex from 'vuex'
import VuexPersist from 'vuex-persist'

Vue.use(Vuex)

export const vuexPersist = new VuexPersist({
  key: 'mla-ptool',
  storage: window.localStorage
})

const getDefaultState = () => {
  return {
    completed: false,
    resetData: false,
    resetDisable: false,
    activeTab: null,
    st: 1,
    errors: [],
    errorAlert: false,
    rules: {
      required: (value:any) => !!value || value === 0 || 'Required.',
      notNegative: (value:number) => value >= 0 || 'No negative values.'
    },
    steps: [
      {
        name: 'Step 1: Soil testing',
        desc:
          'Use soil testing to assess the P-fertility status of your soil.',
        icon: 'waves',
        color: 'primary'
      },
      {
        name: 'Step 2: Stocking rate / soil fertility',
        desc:
          'Determine the stocking rate that is appropriate for your current or projected soil fertility level.',
        icon: 'trending_up',
        color: 'primary darken-1'
      },
      {
        name: 'Step 3: Phosphorus application',
        desc:
          'Determine how much P needs to be applied given your projected stocking rate and soil P status.',
        icon: 'bar_chart',
        color: 'primary darken-2'
      },
      {
        name: 'Step 4: Other factors.',
        desc:
          'Think about other factors that might modify the expected pasture response to P.',
        icon: 'wb_incandescent',
        color: 'primary darken-4'
      },
      {
        name: 'Step 5: Budget analysis',
        desc:
          'Check that the proposed investment in P-fertiliser and/or livestock will generate an acceptable return.',
        icon: 'attach_money',
        color: 'primary darken-3'
      }
    ],
    step1: {
      type: 'Colwell',
      pbi: 80 as number,
      value: 17 as number,
      colwellP: 31.46 as number,
      olsenP: 15 as number,
      alt: false as boolean
    },
    step2: {
      avgGrowingSeason: 7.5 as number,
      potCarryingCapacity: 20 as number,
      targetChartLabelX: 73 as number,
      targetChartLabelY: 237 as number,
      predictedChartLabelX: 40 as number,
      predictedChartLabelY: 40 as number,
      preCarryingCapacity: {
        large: 17.2 as number,
        small: 20 as number
      },
      typical: {
        pTest: 10 as number,
        carryingCapacity: 6 as number
      },
      potential: {
        carryingCapacity: 12 as number
      },
      maxP: 32 as number,
      target: 13 as number,
      stockingRateTarget: 7.9 as number,
      stockingRateCurrent: 10 as number,
      fertilityTarget: 20.9 as number,
      type: 'stockingRate'
    },
    step3: {
      soilLoss: 'Medium',
      slope: 'Easy',
      grazing: 'Set',
      pasture: 'Native',
      rainfall: 700 as number,
      concentration: 9 as number,
      years: 2 as number,
      DSE: 0.94 as number,
      fertiliserRate: 187 as number,
      maintenanceFertiliserRate: 136 as number,
      exportResults: null
    },
    step4: {},
    step5: {
      stockingRateCurrent: 10 as number,
      fertRate: 0 as number,
      currentGrossMargin: 0 as number,
      projectedGrossMargin: 0 as number,
      extraLivestockCost: 0 as number,
      fertCost: 0 as number,
      fertSpread: 0 as number,
      interestOnDebt: 0 as number,
      pastureArea: 0 as number,
      presets: [],
      activePreset: null,
      exportDataEntry: null,
      exportResults: null
    }
  }
}

const state = getDefaultState()

export default new Vuex.Store({
  state: state,
  mutations: {
    resetState (state: any, clearLocalStorage) {
      if (!clearLocalStorage) {
        const presets = JSON.parse(window.localStorage['mla-ptool']).step5.presets
        Object.assign(state, getDefaultState())
        state.step5.presets = presets
      } else {
        localStorage.removeItem('mla-ptool');
        Object.assign(state, getDefaultState())
      }
    },
    resetDisable (state: any, disable: boolean) {
      state.resetDisable = disable
    },
    step1 (state, updatedState) {
      state.step1 = updatedState
    },
    step2 (state, updatedState) {
      state.step2 = updatedState
    },
    step3 (state, updatedState) {
      state.step3 = updatedState
    },
    step4 (state, updatedState) {
      state.step4 = updatedState
    },
    step5 (state, updatedState) {
      state.step5 = updatedState
    }
  },
  actions: {
    resetState ({ commit }, clearLocalStorage) {
      commit('resetState', clearLocalStorage)
    },
    resetDisable ({ commit }, disable) {
      commit('resetDisable', disable)
    },
    updateStep1 ({ commit }, updatedState) {
      commit('step1', updatedState)
    },
    updateStep2 ({ commit }, updatedState) {
      commit('step2', updatedState)
    },
    updateStep3 ({ commit }, updatedState) {
      commit('step3', updatedState)
    },
    updateStep4 ({ commit }, updatedState) {
      commit('step4', updatedState)
    },
    updateStep5 ({ commit }, updatedState) {
      commit('step5', updatedState)
    }
  },
  getters: {
		presets: state => {
			return state.step5.presets
		},
  },
  plugins: [vuexPersist.plugin],
  modules: {}
})
